import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api";
import { Icon, Empty, Spinner } from "../../components/ui";

// Known action types → friendly label + colour. Unknown ones still show (fallback below).
const ACTION_META = {
  LOGIN:        { label: "Signed in",       cls: "au-a-blue" },
  IMPORT:       { label: "Imported records", cls: "au-a-indigo" },
  ASSIGN:       { label: "Assigned",         cls: "au-a-indigo" },
  STAGE_MOVE:   { label: "Moved stage",      cls: "au-a-slate" },
  WORK:         { label: "Worked a record",  cls: "au-a-green" },
  HELP:         { label: "Help request",     cls: "au-a-amber" },
  USER_CREATE:  { label: "Created user",     cls: "au-a-green" },
  USER_UPDATE:  { label: "Updated user",     cls: "au-a-amber" },
  USER_DELETE:  { label: "Deleted user",     cls: "au-a-red" },
  DATA_CHANGE:  { label: "Changed data",     cls: "au-a-slate" },
  ARCHIVE:      { label: "Archived",         cls: "au-a-slate" },
  SESSION_REVOKE:{ label: "Force sign-out",  cls: "au-a-red" },
  AUDIT_SEND:   { label: "Sent for audit",   cls: "au-a-indigo" },
  PASSWORD:     { label: "Set password",     cls: "au-a-amber" },
};
const ROLE_LABEL = { SUPER_ADMIN: "Super Admin", ADMIN: "Admin", SUPERVISOR: "Supervisor", USER: "Production", CLIENT_ADMIN: "Client Admin", AUDITOR: "Auditor" };
const FILTER_ACTIONS = ["", ...Object.keys(ACTION_META)];

const pad = (n) => String(n).padStart(2, "0");
function fmtDate(t) { try { const d = new Date(t); return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`; } catch { return "—"; } }
function fmtTime(t) { try { return new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return ""; } }
function actionMeta(a) { return ACTION_META[a] || { label: (a || "Event").replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase()), cls: "au-a-slate" }; }

// One user's assigned unique-IDs, with a small search box to find a specific ID.
function AssignedIdsBlock({ uname, ids }) {
  const [q, setQ] = useState("");
  const shown = q.trim() ? ids.filter((u) => String(u).toLowerCase().includes(q.trim().toLowerCase())) : ids;
  return (
    <div style={{ minWidth: 220, background: "#f8fafc", border: "1px solid #e5e9f0", borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 800, color: "#1f2733" }}>{uname}</span>
        <span style={{ background: "#eef2ff", color: "#4f46e5", borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 800 }}>{ids.length}</span>
      </div>
      <input value={q} onChange={(ev) => setQ(ev.target.value)} placeholder="Search IDs…"
        onClick={(ev) => ev.stopPropagation()}
        style={{ width: "100%", height: 28, marginBottom: 6, padding: "0 8px", fontSize: 12, border: "1px solid #e5e9f0", borderRadius: 6, boxSizing: "border-box" }} />
      <div style={{ maxHeight: 220, overflow: "auto" }}>
        {shown.length === 0
          ? <div style={{ fontSize: 12, color: "#94a3b8", padding: "2px 0" }}>No match.</div>
          : shown.map((u, i) => <div key={i} className="mono" style={{ fontSize: 12, padding: "1px 0" }}>{u}</div>)}
      </div>
    </div>
  );
}

export function AuditLog() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [users, setUsers] = useState([]);
  const [action, setAction] = useState("");
  const [actorId, setActorId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");   // the term actually sent to the server
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);   // audit row whose unique IDs are expanded

  // Debounce the search box and reset to the first page, so the server filters the WHOLE date range
  // (summary, details — including assigned IDs — targetId and who) in one query, instead of the old
  // client-side filter that only matched the page you were looking at.
  useEffect(() => {
    const t = setTimeout(() => { setQDebounced(q.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { api.listUsers().then(setUsers).catch(() => {}); }, []);
  const load = useCallback(async (silent) => {
    if (!silent) { setErr(""); setData(null); }
    try {
      const params = { page, size: 200 };
      if (action) params.action = action;
      if (actorId) params.actorId = actorId;
      if (from) params.from = from;
      if (to) params.to = to;
      if (qDebounced) params.q = qDebounced;
      const res = await api.auditSearch(params, silent ? { silent: true } : undefined);
      setData(res); setErr("");
    } catch (e) { if (!silent) { setErr(e.message); setData({ items: [] }); } }
  }, [action, actorId, from, to, qDebounced, page]);
  useEffect(() => { load(false); }, [load]);

  // Live view: silently re-pull the audit trail every 10s so new events appear on their own —
  // no spinner, your filters and page stay put. Paused while the browser tab is in the background.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) load(true); }, 10000);
    return () => clearInterval(id);
  }, [load]);

  // The server already applied the text search across the full date range, so render its rows as-is
  // (filtering here again would re-introduce the "only matches this page" bug).
  const items = data?.items || [];
  const clearAll = () => { setAction(""); setActorId(""); setFrom(""); setTo(""); setQ(""); setQDebounced(""); setPage(0); };

  return (
    <div className="jr-page">
      <div style={{ marginBottom: 12, display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0 }}>Audit trail</h2>
          <div className="cell-sub">Every action taken in the tool — by anyone, including admins and super-admins — with the exact date and time.</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => load(true)}
          title="Auto-refreshes every 10 seconds — click to refresh now"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", display: "inline-block" }} /> ↻ Refresh · Live every 10s
        </button>
      </div>

      <div className="au-filters">
        <select className="inp" value={actorId} onChange={(e) => { setActorId(e.target.value); setPage(0); }}>
          <option value="">All people</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
        </select>
        <select className="inp" value={action} onChange={(e) => { setAction(e.target.value); setPage(0); }}>
          {FILTER_ACTIONS.map((a) => <option key={a} value={a}>{a ? actionMeta(a).label : "All actions"}</option>)}
        </select>
        <label className="au-date"><span>From</span><input className="inp" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} /></label>
        <label className="au-date"><span>To</span><input className="inp" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} /></label>
        <div className="inv-search"><Icon.search width="15" height="15" /><input className="inp" placeholder="Search anything…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <button className="link-btn" onClick={clearAll}>Clear</button>
      </div>

      {err && <div className="import-err"><Icon.x width="15" height="15" /><span>{err}</span></div>}

      <div className="card cmd-card"><div className="table-wrap">
        <table className="tbl cmd-table audit-tbl">
          <thead><tr>
            <th>Date</th><th>Time</th><th>Who</th><th>Role</th><th>Action</th><th>What happened</th>
          </tr></thead>
          <tbody>
            {data === null ? (
              <tr><td colSpan="6"><div className="empty"><Spinner /></div></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: "center", color: "#94a3b8", padding: "28px 0" }}>No events for these filters.</td></tr>
            ) : items.map((e) => { const m = actionMeta(e.action); return (
              <tr key={e.id}>
                <td className="cell-sub" style={{ whiteSpace: "nowrap" }}>{fmtDate(e.at)}</td>
                <td className="mono" style={{ whiteSpace: "nowrap" }}>{fmtTime(e.at)}</td>
                <td className="cell-strong">{e.actorName || "system"}</td>
                <td className="cell-sub">{ROLE_LABEL[e.actorRole] || e.actorRole || "—"}</td>
                <td><span className={"au-pill " + m.cls}>{m.label}</span></td>
                <td className="wrap">
                  {e.details
                    ? <span style={{ cursor: "pointer", color: "#4f46e5", fontWeight: 600 }} onClick={() => setOpenId(openId === e.id ? null : e.id)}>
                        {e.summary || "—"} <span style={{ fontSize: 11 }}>{openId === e.id ? "▲ hide IDs" : "▾ show IDs"}</span>
                      </span>
                    : (e.summary || "—")}
                  {e.targetId && <span className="au-target"> · {e.targetId}</span>}
                  {openId === e.id && e.details && (() => {
                    let byUser = null;
                    try { const parsed = JSON.parse(e.details); if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) byUser = parsed; } catch { byUser = null; }
                    return (
                      <div style={{ marginTop: 6, display: "flex", gap: 14, flexWrap: "wrap", maxHeight: 300, overflow: "auto" }}>
                        {byUser
                          ? Object.entries(byUser).map(([uname, ids]) => (
                              <AssignedIdsBlock key={uname} uname={uname} ids={ids || []} />
                            ))
                          : <div style={{ background: "#f8fafc", border: "1px solid #e5e9f0", borderRadius: 8, padding: "8px 10px" }}>{e.details.split("\n").filter(Boolean).map((u, i) => <div key={i} className="mono" style={{ fontSize: 12, padding: "1px 0" }}>{u}</div>)}</div>}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div></div>

      {data && (data.total > (data.items || []).length || page > 0) && (
        <div className="inv-pager" style={{ marginTop: 12 }}>
          <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Newer</button>
          <span className="cell-sub" style={{ margin: "0 12px" }}>Page {page + 1}</span>
          <button className="btn btn-ghost btn-sm" disabled={(data.items || []).length < 200} onClick={() => setPage((p) => p + 1)}>Older ›</button>
        </div>
      )}
    </div>
  );
}